import { Link } from "wouter";

import { StripMarkdown } from "@/components/markdown/StripMarkdown.tsx";
import { compactRowClass, getThreadItemInteractionStyles } from "@/components/threads/shared.tsx";
import { GradientDefinition } from "@/components/ui/gradient-def.tsx";
import { cn } from "@/lib/utils.ts";
import { type TaskIndicator } from "@/shared/taskFilterLabels.ts";
import { dueShort } from "@/shared/userTaskDue.ts";
import { userTaskHref } from "@/types/userTaskLinks.ts";
import type { UserTask } from "@/types/userTasks.ts";
import { INDICATOR } from "./UserTaskRow.tsx";
import { useTaskArrival } from "./useTaskArrival.ts";

/**
 * One task as a single line: what kind it is, what it is, and when it is due.
 *
 * The thread list's compact row, the one it draws on mobile web: the same 44px
 * white surface from {@link compactRowClass}, the same 14px mark slot, the same
 * truncating title column with its second line run on after it, and a date
 * where that list puts its own. The two lists sit a tab apart and are read the
 * same way — down the left edge, for the one that needs you — so a line in one
 * is the same object as a line in the other rather than a second thing that
 * resembles it.
 *
 * The mark is the glyph alone, where a card spells the kind out beside it. A
 * column of the same four glyphs is read once and then scanned; a column of
 * the same four words is read every time. The word is still said for a screen
 * reader, which gets neither the glyph nor its colour.
 *
 * The whole line opens the task. There are no buttons on it — a row this
 * height has no room for three and an overflow, and the page the line opens
 * carries every answer. The card view is one click away for a reader who
 * wants them in the list.
 */
export const CompactTaskRow = ({
  task,
  group,
  focused = false,
}: {
  task: UserTask;
  /** Which group this row belongs to; see `UserTaskRow`'s own `group`. */
  group?: TaskIndicator;
  focused?: boolean;
}) => {
  const {
    Icon,
    className: markClass = "text-muted-foreground",
    gradientIcon,
  } = (group && INDICATOR[group.key]) ?? {};
  const due = dueShort(task, Date.now());
  // Weighted for the rows blocked on the reader, as the thread list weights
  // its own, and read from the same table that picks the mark so the two
  // cannot disagree about which rows those are.
  const needsYou = group?.key === "needsYou";
  const context = task.context?.text ?? task.description;
  const { ref, flash, onAnimationEnd } = useTaskArrival<HTMLLIElement>(task.id, focused);

  return (
    <li ref={ref} data-task-id={task.id} onAnimationEnd={onAnimationEnd}>
      <Link
        href={userTaskHref(task.id)}
        // The same glow the card wears, once, where a chip or a link sent
        // the list: the line is as easy to lose below the fold as a card.
        className={cn(
          compactRowClass,
          getThreadItemInteractionStyles(focused),
          flash && "animate-task-flash",
        )}
        data-statsig-name="tasks-compact-open"
      >
        {/* The mark, centered in a fixed slot rather than sitting at its own
            width, so the marks line up down the list — the thread row holds
            its status dot in this same slot. */}
        <span className="flex w-3.5 shrink-0 justify-center">
          {/* `background-clip: text` reaches text and nothing else, so a glyph
              given the gradient as a class inherits `currentColor` —
              transparent — and draws as nothing. The shared definition gives
              its stroke the same sweep instead, as `GroupMark` does. */}
          {gradientIcon && <GradientDefinition />}
          {Icon && (
            <Icon
              className={cn("size-3.5", gradientIcon ? "gradient-brand-purple-stroke" : markClass)}
              aria-hidden
            />
          )}
        </span>
        {group?.label && <span className="sr-only">{group.label}. </span>}
        {/* Title and context. `pl-2` is the gap from the mark column, which has
            to come from here: the marks carry no ground of their own. `pr-1`
            keeps the trailing edge tighter, where the date sits against the
            row's padding. */}
        <div className="min-w-0 flex-1 truncate pl-2 pr-1 text-sm">
          <span className={needsYou ? "font-semibold" : "font-normal"}>{task.title}</span>
          {context && (
            <>
              &ensp;
              <span className="hidden @md:inline">-&ensp;</span>
              {/* Stripped rather than printed: okthx writes this line and its
                  emphasis arrives as markdown, which a single row has no room
                  to lay out and which reads as literal asterisks untouched. */}
              <span className="text-muted-foreground">
                <StripMarkdown>{context}</StripMarkdown>
              </span>
            </>
          )}
        </div>
        {due && (
          <span
            className={cn("shrink-0 text-sm text-muted-foreground", due.overdue && "font-semibold")}
          >
            {/* The glance gets the short date. The weight is otherwise the
                only thing saying the task is late, which is exactly the reader
                it would miss, so the whole sentence is said here instead. */}
            <span aria-hidden>{due.text}</span>
            <span className="sr-only">{due.spoken}</span>
          </span>
        )}
      </Link>
    </li>
  );
};
